import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Stack,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import useCifaStore, { RecursoFinanceiro } from 'stores/cifa/cifa.store';
import { CifaStatusEnum } from 'enum/cifaStatus.enum';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';

interface Step2Props {
  data: {
    recursos: RecursoFinanceiro[];
    rcl: number;
  };
  onBack: () => void;
  onConfirm: () => Promise<void>;
  loading: boolean;
}

const Step2: React.FC<Step2Props> = ({
  data,
  onBack,
  onConfirm,
  loading: parentLoading,
}) => {
  const { setPartialForm } = useCifaStore();
  const [localData, setLocalData] = useState(data);
  const [editItem, setEditItem] = useState<RecursoFinanceiro | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
  const match = /cifa-(\d{4})/.exec(location.pathname);
  const anoReferencia = match ? Number(match[1]) : 2026;

  // Formatting functions
  const formatCurrencyDisplay = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return numValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const parseCurrencyInput = (value: string): number => {
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly ? parseFloat(digitsOnly) / 100 : 0;
  };

  const getRecursoKey = (recurso: RecursoFinanceiro, index: number) => {
    // Gera uma key única baseada nos dados do recurso
    const baseKey = `${recurso.tipo}-${recurso.descricao}`.replace(/\s+/g, '-').toLowerCase();
    return `${baseKey}-${index}`;
  };

  // Financial summary calculations
  const financialSummary = useMemo(() => {
    const totalOrcado = localData.recursos.reduce((sum, item) => sum + (item.valor_orcado || 0), 0);
    const totalLiquidado = localData.recursos.reduce((sum, item) => sum + (item.valor_liquidado || 0), 0);

    const percentualExecucao = totalOrcado > 0
      ? (totalLiquidado / totalOrcado) * 100
      : 0;

    const percentualRCL = localData.rcl > 0
      ? (totalLiquidado / localData.rcl) * 100
      : 0;

    return {
      totalOrcado,
      totalLiquidado,
      percentualExecucao: parseFloat(percentualExecucao.toFixed(2)),
      percentualRCL: parseFloat(percentualRCL.toFixed(2))
    };
  }, [localData]);

  const handleAddItem = () => {
    setEditItem({
      tipo: 'Programa',
      descricao: '',
      valor_orcado: 0,
      valor_liquidado: 0,
    });
    setEditIndex(null);
  };

  const handleEditItem = (index: number) => {
    setEditItem({ ...localData.recursos[index] });
    setEditIndex(index);
  };

  const handleSaveItem = () => {
    if (!editItem) return;

    // Validation
    if (!editItem.descricao.trim()) {
      showFeedback('Descrição é obrigatória', 'error');
      return;
    }

    if (editItem.valor_orcado <= 0) {
      showFeedback('Valor orçado deve ser positivo', 'error');
      return;
    }

    if (editItem.valor_liquidado < 0) {
      showFeedback('Valor liquidado não pode ser negativo', 'error');
      return;
    }

    if (editItem.valor_liquidado > editItem.valor_orcado) {
      showFeedback('Valor liquidado não pode ser maior que o valor orçado', 'error');
      return;
    }

    const updatedResources = [...localData.recursos];

    if (editIndex !== null) {
      updatedResources[editIndex] = editItem;
    } else {
      updatedResources.push(editItem);
    }

    updateLocalData(updatedResources);
    showFeedback(editIndex !== null ? 'Item atualizado' : 'Item adicionado', 'success');
    setEditItem(null);
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex === null) return;

    const updatedResources = localData.recursos.filter((_, idx) => idx !== deleteIndex);
    updateLocalData(updatedResources);

    showFeedback('Item removido', 'success');
    setDeleteIndex(null);
  };

  const updateLocalData = (resources: RecursoFinanceiro[]) => {
    const newData = {
      ...localData,
      recursos: resources
    };
    setLocalData(newData);
    setPartialForm('recursos_financeiros_aplicados_detalhamento', resources);
  };

  const handleRCLChange = (value: string) => {
    const newRcl = parseCurrencyInput(value);
    setLocalData(prev => ({ ...prev, rcl: newRcl }));
    setPartialForm('receita_corrente_liquida_2025', newRcl);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setPartialForm('status_aba_5', CifaStatusEnum.APPROVED);
      await onConfirm();
      showFeedback('Dados salvos com sucesso!', 'success');
      setTimeout(() => {
        navigate(ROUTES[`INCLUDE_CIFA_${anoReferencia}` as keyof typeof ROUTES] as string);
      }, 1500);
    } catch (error) {
      console.error('Submission error:', error);
      showFeedback('Erro ao salvar os dados', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Feedback helpers
  const showFeedback = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeFeedback = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1152px', margin: '0 auto' }}>
      {/* Financial Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { title: 'Total Orçado', value: financialSummary.totalOrcado, color: '#e3f2fd' },
          { title: 'Total Liquidado', value: financialSummary.totalLiquidado, color: '#e8f5e9' },
          { title: '% Execução', value: financialSummary.percentualExecucao, suffix: '%', color: '#fff3e0' },
          { title: '% da RCL', value: financialSummary.percentualRCL, suffix: '%', color: '#f3e5f5' }
        ].map((card, index) => (
          <Grid item xs={12} md={3} key={card.title.replace(/\s+/g, '-').toLowerCase()}>
            <Paper elevation={3} sx={{ p: 2, textAlign: 'center', backgroundColor: card.color }}>
              <Typography variant="subtitle2">{card.title}</Typography>
              <Typography variant="h6" fontWeight="bold">
                {card.suffix ? `${card.value}${card.suffix}` : formatCurrencyDisplay(card.value)}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Resources Table */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" fontWeight="bold">
            Recursos Financeiros
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
            disabled={isSubmitting || parentLoading}
          >
            Adicionar
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell><strong>Tipo</strong></TableCell>
                <TableCell><strong>Descrição</strong></TableCell>
                <TableCell align="right"><strong>Orçado (R$)</strong></TableCell>
                <TableCell align="right"><strong>Liquidado (R$)</strong></TableCell>
                <TableCell align="right"><strong>% Exec.</strong></TableCell>
                <TableCell align="center"><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {localData.recursos.map((item, index) => {
                const executionPercent = item.valor_orcado > 0
                  ? ((item.valor_liquidado / item.valor_orcado) * 100).toFixed(2)
                  : '0.00';

                return (
                  <TableRow key={getRecursoKey(item, index)} hover>
                    <TableCell>{item.tipo}</TableCell>
                    <TableCell>{item.descricao}</TableCell>
                    <TableCell align="right">{formatCurrencyDisplay(item.valor_orcado)}</TableCell>
                    <TableCell align="right">{formatCurrencyDisplay(item.valor_liquidado)}</TableCell>
                    <TableCell align="right">{executionPercent}%</TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleEditItem(index)}
                        disabled={isSubmitting || parentLoading}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => setDeleteIndex(index)}
                        disabled={isSubmitting || parentLoading}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {localData.recursos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nenhum recurso cadastrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* RCL Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Receita Corrente Líquida (RCL) 2025
        </Typography>
        <TextField
          label="Valor Total da RCL"
          value={localData.rcl.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
          onChange={(e) => handleRCLChange(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            inputProps: { inputMode: 'numeric' }
          }}
        />
        <Typography variant="body2" color="text.secondary" mt={1}>
          {financialSummary.percentualRCL}% da RCL investido
        </Typography>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editIndex !== null ? 'Editar Recurso' : 'Adicionar Recurso'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo *</InputLabel>
              <Select
                value={editItem?.tipo ?? ''}
                label="Tipo *"
                onChange={(e) => editItem && setEditItem({
                  ...editItem,
                  tipo: e.target.value as 'Programa' | 'Projeto' | 'Ação/Atividade'
                })}
              >
                {['Programa', 'Projeto', 'Ação/Atividade'].map((tipo) => (
                  <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Descrição *"
              value={editItem?.descricao ?? ''}
              onChange={(e) => editItem && setEditItem({
                ...editItem,
                descricao: e.target.value
              })}
              fullWidth
              multiline
              rows={3}
            />

            <TextField
              label="Valor Orçado *"
              value={editItem?.valor_orcado.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) ?? ''}
              onChange={(e) => editItem && setEditItem({
                ...editItem,
                valor_orcado: parseCurrencyInput(e.target.value)
              })}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                inputProps: { inputMode: 'numeric' }
              }}
            />

            <TextField
              label="Valor Liquidado"
              value={editItem?.valor_liquidado.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) ?? ''}
              onChange={(e) => editItem && setEditItem({
                ...editItem,
                valor_liquidado: parseCurrencyInput(e.target.value)
              })}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                inputProps: { inputMode: 'numeric' }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveItem}
            disabled={isSubmitting || parentLoading}
          >
            {editIndex !== null ? 'Atualizar' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteIndex !== null} onClose={() => setDeleteIndex(null)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir este recurso?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteIndex(null)}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isSubmitting || parentLoading}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={closeFeedback}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Navigation Buttons */}
      <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{ minWidth: '120px' }}
          disabled={isSubmitting || parentLoading}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ minWidth: '120px' }}
          disabled={isSubmitting || parentLoading || localData.recursos.length === 0}
          endIcon={isSubmitting ? <CircularProgress size={24} /> : null}
        >
          {isSubmitting ? 'Salvando...' : 'Confirmar'}
        </Button>
      </Stack>
    </Box>
  );
};

export default Step2;
